import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useChatboxMutation, useReactMessageMutation, useReportIssueMutation, useSaveTopicMutation } from '../../store/chatbox/chatboxApi';
import { classNames } from '../../utils/classNames';
import { useTheme } from '../../utils/theme-context';
import ReportModal from '../shared/report-modal/ReportModal';
import { DEVICE_INFO_API_URL } from '../../config';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCurrentUserQuery } from '../../store/user/userApi';
import Loading from '../../shared/Loading';
import { setClarifyMessage } from '../../store/slices/chats.slice';
import { useAppDispatch } from '../../app/hooks';

type TActive = 'copy' | 'thumb-up' | 'thumb-down' | 'save' | 'report'

const MessageActions = ({
	chatId,
	message,
	liked,
	disliked,
	favorite,
	topicId,
	isIssue,
	audioRef,
	showOptions,
	clarify
}: {
	chatId: string,
	message: string,
	topicId?: string,
	liked?: boolean,
	disliked?: boolean,
	favorite?: boolean,
	isIssue?: boolean,
	audioRef?: MutableRefObject<HTMLAudioElement | { pause: () => void; paused: boolean; currentTime: number } | null>,
	showOptions?: boolean
	clarify?: boolean
}) => {
	const { data: userInfo } = useGetCurrentUserQuery();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const abortControllerRef = useRef<AbortController | null>(null);
	const objectUrlRef = useRef<string | null>(null); // optional if using MediaSource
	const [isVoiceLoading, setIsVoiceLoading] = useState<boolean>();
	const { theme } = useTheme();
	const [active, setActive] = useState<TActive[]>([]);
	const [reportModalOpen, setReportModalOpen] = useState(false)
	const [selectedIssue, setSelectedIssue] = useState('')
	const [saveTopic, { isLoading }] = useSaveTopicMutation();
	const [isSpeaking, setIsSpeaking] = useState(false);
	const [chatBoxAdd, { isLoading: clarifyLoading }] = useChatboxMutation()
	const [reactMessage, { isLoading: isReactLoading }] = useReactMessageMutation();
	const [reportIssue, { isLoading: isReportLoading }] = useReportIssueMutation();

	useEffect(() => {
		let tempActives = [...active]
		if (liked)
			tempActives = ([...tempActives, 'thumb-up'])
		if (disliked)
			tempActives = ([...tempActives, 'thumb-down'])
		if (favorite) 
			tempActives = ([...tempActives, 'save'])

		setActive(tempActives);
	}, [liked, disliked, favorite])

	const stripHtml = (html: string): string => {
		const div = document.createElement('div');
		div.innerHTML = html;
		return div.textContent || div.innerText || '';
	};

	const cancelStreaming = () => {
		// On unmount, stop audio
		if (audioRef && audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}

		// Abort the fetch stream
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		// Revoke any object URLs if needed
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
	}

	useEffect(() => {
		return () => {
			cancelStreaming();
		};
	}, []);


	const speakText = async () => {
		if (isVoiceLoading) return;

		try {
			cancelStreaming()
			setIsVoiceLoading(true);

			const controller = new AbortController();
			abortControllerRef.current = controller;

			const response = await fetch(
				`${DEVICE_INFO_API_URL}/tts/stream?text=${encodeURIComponent(message)}&voice=shimmer`,
				{ signal: controller.signal }
			);

			if (!response.body) {
				throw new Error('No stream body in response');
			}

			const mimeCodec = 'audio/mpeg';
			if (!MediaSource.isTypeSupported(mimeCodec)) {
				throw new Error('Codec not supported');
			}

			const mediaSource = new MediaSource();
			const audio = new Audio();
			const objectUrl = URL.createObjectURL(mediaSource);
			objectUrlRef.current = objectUrl;
			audio.src = objectUrl;
			if (audioRef)
			audioRef.current = audio;

			mediaSource.addEventListener('sourceopen', async () => {
				const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
				const reader = response.body!.getReader();

				const readChunk = async () => {
					try {
						const { done, value } = await reader.read();
						if (done) {
							mediaSource.endOfStream();
							return;
						}
						sourceBuffer.appendBuffer(value);
					} catch (e) {
						// console.warn('Read aborted or failed:', e);
					}
				};

				sourceBuffer.addEventListener('updateend', readChunk);
				await readChunk();
			});

			audio.play();
			setIsSpeaking(true);

			audio.addEventListener('ended', () => {
				setIsSpeaking(false);
			});

		} catch (e) {
			const error = e as {name: string}
			if (error.name === 'AbortError') {
				// console.log('Streaming aborted');
			} else {
				// console.error('Streaming error:', e);
			}
		} finally {
			setIsVoiceLoading(false);
		}
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(stripHtml(message));
			setActive((prev) => [...prev, 'copy']);
			setTimeout(() => {
				setActive((prev) => prev.filter((ac) => ac !== 'copy'));
			}, 2000);
		} catch (err) {
			console.error('copy error:', err);
		}
	};

	const handleThumbUp = async () => {
		try {
			await reactMessage({ messageId: chatId, action: 'like' }).unwrap()
			const getActive = active.includes('thumb-up') ? [...active].filter(ac => ac !== 'thumb-up') : [...active, 'thumb-up' as TActive].filter(ac => ac !== 'thumb-down');
			setActive(getActive);
		} catch (e) {
			// console.log(e)
		}
	}

	const handleThumbDown = async () => {
		try {
			await reactMessage({ messageId: chatId, action: 'dislike' }).unwrap()
			const getActive = active.includes('thumb-down') ? [...active].filter(ac => ac !== 'thumb-down') : [...active, 'thumb-down' as TActive].filter(ac => ac !== 'thumb-up');;
			setActive(getActive);
		} catch (e) {
			// console.log(e)
		}
	}

	const handleSave = async () => {
		try {
			await reactMessage({ messageId: chatId, action: 'favorite' }).unwrap()
			const getActive = active.includes('save') ? [...active].filter(ac => ac !== 'save') : [...active, 'save' as TActive];
			setActive(getActive);
			await saveTopic(chatId).unwrap()
		} catch (e) {
			// console.log(e)
		}
	}

	
	const onCloseReportModal = () => {
		setSelectedIssue('')
		setReportModalOpen(false)
	}

	const handleReportIssue = async () => {
		try {
			await reportIssue({ messageId: chatId, userIssue: selectedIssue, topicId: topicId || '' }).unwrap()
			onCloseReportModal()
			setActive([...active, 'report']);
			await saveTopic(chatId).unwrap()
		} catch (e) {
			// console.log(e)
		}
	}

	const onClickReportIssue = () => {
		setReportModalOpen(true)
	}

	const handleNewTopic = () => {
		if (location.pathname + location.search === '/home?newChat=true') {
			navigate(0);
		} else {
			navigate('/home?newChat=true');
		}
	}
	const handleClarify = async () => {
		const user = { name: userInfo?.name || '', email: userInfo?.email || '', id: userInfo?.id || '' }
		setTimeout(() => {
			document.querySelector('#chatEnd')?.scrollIntoView({ behavior: 'smooth' });
		}, 300)

		dispatch(setClarifyMessage(chatId));
		await chatBoxAdd(
			topicId ? {
				topic: topicId, message: 'clarify', user, clarify: true
			} : { message: 'clarify', user, clarify: true }
		).unwrap();
		
	}

	const btnClassNames = 'p-2 hover:bg-gray-200 rounded-md dark:hover:bg-gray-600';
	return (
		<>
		<div className='mt-1'>
			 <ReportModal open={reportModalOpen} onClose={onCloseReportModal} setSelectedIssue={setSelectedIssue} handleReportIssue={handleReportIssue} selectedIssue={selectedIssue} isReportLoading={isReportLoading}/>
			{/* <p className='mb-1'>Let me know how you find this response.</p> */}
			<button onClick={handleCopy} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} title='copy' type='button'>
				{active.includes('copy') ? <img alt='copy' src={theme === 'dark' ? '/svg/icons/copy-white.svg' : '/svg/icons/active/copy-active.svg'} className='object-contain w-[22px]' /> : <img alt='copy' src={theme === 'dark' ? '/svg/icons/copy-white.svg' : '/svg/icons/copy.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={handleThumbUp} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isReactLoading} title='thumbs up' type='button'>
				{active.includes('thumb-up') ? <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/active/thumb-up-white.svg' : '/svg/icons/active/thumb-up.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/thumb-up-white.svg' : '/svg/icons/thumb-up.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={handleThumbDown} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isReactLoading} title='thumbs down' type='button'>
				{active.includes('thumb-down') ? <img alt='thumb down' src={theme === 'dark' ? '/svg/icons/active/thumb-down-white.svg' : '/svg/icons/active/thumb-down.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/thumb-down-white.svg' : '/svg/icons/thumb-down.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={handleSave} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isLoading} title='save' type='button'>
				{active.includes('save') ? <img alt='save' src={theme === 'dark' ? '/svg/icons/active/save-active-white.svg' : '/svg/icons/active/save-active.svg'} className='object-contain w-[22px]' /> : <img alt='thumb up' src={theme === 'dark' ? '/svg/icons/save-white.svg' : '/svg/icons/save.svg'} className='object-contain w-[22px]' />}
			</button>
			<button onClick={onClickReportIssue} className={classNames(btnClassNames, isLoading ? 'opacity-50' : '')} disabled={isLoading} title='report' type='button'>
				<img alt='report' src={theme === 'dark' ? '/svg/icons/active/report-white.svg' : '/svg/icons/active/report-active.svg'} className='object-contain w-[22px]' />
			</button>
			<button onClick={speakText} className={btnClassNames} style={{opacity: isVoiceLoading ? '0.5' : '1'}} title='Read aloud'>
				{isSpeaking ? <img alt='audio' src={theme === 'dark' ? '/svg/icons/active/audio-active-white.svg' : '/svg/icons/active/audio-active.svg'} className='object-contain w-[22px]' /> : <img alt='audio' src={theme === 'dark' ? '/svg/icons/audio-white.svg' : '/svg/icons/audio.svg'} className='object-contain w-[22px]' />}
			</button>
		</div>
		{showOptions && <div className='mt-2 border-t border-dashed flex flex-wrap gap-2 py-2'>
			<button disabled={clarify} type='button' onClick={handleNewTopic} className='border border-gray-200 bg-gray-100 hover:bg-gray-200 shadow-sm py-1.5 px-3 flex items-center justify-center rounded-2xl text-gray-800 font-medium text-base'>
				<img src="/svg/icons/topic.svg" className='mr-1' alt="topic" />
				New Topic
			</button>
			<button disabled={clarify} type='button' onClick={handleClarify} className={classNames('border border-gray-200 shadow-sm py-1.5 px-3 flex items-center justify-center rounded-2xl font-medium text-base', clarify ? 'bg-[#10B981] text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800')}>
				<img src={clarify ? '/svg/icons/active/clarify.svg' : '/svg/icons/clarify.svg'} className='mr-1' alt="clarify" />
				{clarifyLoading && <Loading />}
				Clarify
			</button>
		</div>}
		</>
	)
}

export default MessageActions;